// features/fund/table/VestingRuleTable.jsx
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Flex, Form, Input, InputNumber, Select } from "antd";
import { useTranslation } from "react-i18next";
import styles from "../styles/Fund.module.css";
import { TIME_UNIT, emptyVestingRule, getCriteriaColumnLabels, toOptions } from "../schemas/FundSchemas";
import { FormInput, FormSelect } from "components/form/FormProvide";

const VestingRuleTable = ({ positionOptions = [], disabled = false }) => {
    const { t, i18n } = useTranslation();
    const form = Form.useFormInstance();
    const unitOptions = toOptions(TIME_UNIT, i18n.language);

    // Column wording follows "เกณฑ์การได้รับสิทธิในเงินสมทบ" (อายุงาน / อายุสมาชิก)
    const benefitEligibilityCriteria = Form.useWatch("benefitEligibilityCriteria");
    const columnLabels = getCriteriaColumnLabels(benefitEligibilityCriteria, i18n.language);

    // "ตำแหน่งงาน" column only appears when this block's own "กำหนดสิทธิ" is ticked AND scope = ตามตำแหน่ง
    const definedByPosition = Form.useWatch(["vestingConditions", "definedByPosition"]);
    const scope = Form.useWatch(["vestingConditions", "scope"]);
    const showPositionColumn = definedByPosition && scope === "POSITION";

    const handlePositionChange = (rowName, value) => {
        const label = positionOptions.find((o) => o.value === value)?.label;
        form.setFieldValue(["vestingConditions", "rules", rowName, "positionName"], label);
    };

    return (
        <Form.List name={["vestingConditions", "rules"]}>
            {(fields, { add, remove }) => (
                <>
                    <table className={styles.ruleTable}>
                        <thead>
                            <tr>
                                {showPositionColumn && <th>{t("fund.label.position") || "ตำแหน่งงาน"}</th>}
                                <th>{columnLabels.from}</th>
                                <th>{t("fund.label.unit") || "หน่วย"}</th>
                                <th>{columnLabels.to}</th>
                                <th>{t("fund.label.unit") || "หน่วย"}</th>
                                <th>{t("fund.label.employeeBenefitPercent") || "พนง.ได้รับผลประโยชน์ %"}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map(({ key, name, ...restField }) => (
                                <tr key={key}>
                                    {showPositionColumn && (
                                        <td>
                                            <FormSelect
                                                {...restField}
                                                disabled={disabled}
                                                name={[name, "positionId"]}
                                                required
                                                style={{ marginBottom: 0, width: 140 }}
                                                options={positionOptions}
                                                placeholder={t("fund.placeholder.please_select") || "กรุณาเลือก"}
                                                onChange={(value) => handlePositionChange(name, value)}
                                            >
                                            </FormSelect>
                                            <Form.Item {...restField} name={[name, "positionName"]} hidden>
                                                <Input />
                                            </Form.Item>
                                        </td>
                                    )}
                                    <td>
                                        <FormInput
                                            disabled={disabled}
                                            {...restField}
                                            name={[name, "serviceFrom"]}
                                            // rules={[{ required: true, message: "" }]}
                                            style={{ marginBottom: 0 }}
                                            min={0}
                                            placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                                        >
                                            {/* <InputNumber min={0} style={{ width: "100%" }} placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} /> */}
                                        </FormInput>
                                    </td>
                                    <td>
                                        <FormSelect
                                            disabled={disabled}
                                            {...restField}
                                            name={[name, "serviceFromUnit"]}
                                            rules={[{ required: true, message: "" }]}
                                            style={{ marginBottom: 0, width: 96 }}
                                            options={unitOptions}
                                        >
                                        </FormSelect>
                                    </td>
                                    <td>
                                        <FormInput
                                            disabled={disabled}
                                            {...restField}
                                            name={[name, "serviceTo"]}
                                            // rules={[{ required: true, message: "" }]}
                                            style={{ marginBottom: 0 }}
                                            min={0}
                                            placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                                        >
                                            {/* <InputNumber min={0} style={{ width: "100%" }} placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} /> */}
                                        </FormInput>
                                    </td>
                                    <td>
                                        <FormSelect
                                            disabled={disabled}
                                            {...restField}
                                            name={[name, "serviceToUnit"]}
                                            rules={[{ required: true, message: "" }]}
                                            style={{ marginBottom: 0, width: 96 }}
                                            options={unitOptions}
                                        >
                                        </FormSelect>
                                    </td>
                                    <td>
                                        <FormInput
                                            {...restField}
                                            disabled={disabled}
                                            required
                                            name={[name, "employeeBenefitPercent"]}
                                            // rules={[{ required: true, message: "" }]}
                                            style={{ marginBottom: 0 }}
                                            min={0}
                                            placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                                        >
                                            {/* <InputNumber min={0} style={{ width: "100%" }} placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} /> */}
                                        </FormInput>
                                    </td>
                                    <td>
                                        {!disabled && fields.length > 1 && (
                                            <DeleteOutlined className={styles.deleteIcon} onClick={() => remove(name)} />
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!disabled &&
                        <Flex>
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                className={styles.addRowButton}
                                onClick={() => add(emptyVestingRule())}
                            >
                                {t("fund.label.addRow") || "เพิ่มรายการ"}
                            </Button>
                        </Flex>
                    }
                </>
            )}
        </Form.List>
    );
};

export default VestingRuleTable;